import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import en from "../i18n/en";
import ar from "../i18n/ar";
import api from "../api/api";

const LanguageContext = createContext();

// Translations resolve in four layers, cheapest first:
//
//   en JSON  ->  <lang> JSON  ->  global pack delta  ->  tenant override
//
// The first two ship in this bundle, so the app renders correctly with no
// network at all. The API only returns the delta on top of them — admin edits
// and any language that has no bundled JSON.
export const BASELINES = { en, ar };

// Used before /common/i18n/locales/ answers, and whenever it can't be reached
// (logged out, offline, API down). Mirrors BOOTSTRAP_LOCALES on the server.
const FALLBACK_LOCALES = [
  { code: "en", label: "English", native_label: "English", direction: "ltr", font_family: "", sort_order: 0 },
  { code: "ar", label: "Arabic", native_label: "العربية", direction: "rtl", font_family: "sans-serif", sort_order: 1 },
];

const FLAGS = { en: "🇺🇸", ar: "🇸🇦", fr: "🇫🇷", es: "🇪🇸", de: "🇩🇪", ur: "🇵🇰", hi: "🇮🇳", tr: "🇹🇷", zh: "🇨🇳", ru: "🇷🇺" };

const bundleKey = (lang) => `i18n:bundle:${lang}`;

const readCachedBundle = (lang) => {
  try {
    const raw = localStorage.getItem(bundleKey(lang));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.items === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedBundle = (lang, payload) => {
  try {
    localStorage.setItem(bundleKey(lang), JSON.stringify(payload));
  } catch {
    // Quota or private mode — the in-memory copy still works for this session.
  }
};

// The bundle endpoint requires auth. Calling it while logged out would trip the
// 401 interceptor into a pointless refresh attempt, so gate on the same marker
// AuthProvider uses.
const isSignedIn = () => {
  try {
    return !!localStorage.getItem("userdetails");
  } catch {
    return false;
  }
};

const toLanguageMap = (locales) =>
  locales.reduce((acc, l) => {
    acc[l.code] = {
      translations: BASELINES[l.code] || {},
      dir: l.direction || "ltr",
      label: l.label || l.code,
      nativeLabel: l.native_label || l.label || l.code,
      fontFamily: l.font_family || "",
      flag: FLAGS[l.code] || "🌐",
    };
    return acc;
  }, {});

const LanguageProvider = ({ children }) => {
  const [locales, setLocales] = useState(FALLBACK_LOCALES);

  const [language, setLanguage_] = useState(() => {
    try {
      return localStorage.getItem("language") || "en";
    } catch {
      return "en";
    }
  });

  // Hydrated synchronously from localStorage so a reload shows the admin's
  // saved wording on first paint instead of flashing the bundled value.
  const [deltasByLang, setDeltasByLang] = useState(() => {
    const cached = readCachedBundle(localStorage.getItem("language") || "en");
    return cached ? { [cached.lang || "en"]: cached.items } : {};
  });

  const languages = useMemo(() => toLanguageMap(locales), [locales]);
  const config = languages[language] || languages.en || toLanguageMap(FALLBACK_LOCALES).en;

  const setLanguage = useCallback(
    (lang) => {
      if (lang && (languages[lang] || BASELINES[lang])) setLanguage_(lang);
    },
    [languages]
  );

  // Apply dir/lang/font on <html> whenever the active language changes.
  useEffect(() => {
    document.body.classList.add("lang-switching");

    const html = document.documentElement;
    html.setAttribute("dir", config.dir);
    html.setAttribute("lang", language);
    // Set inline so a locale-specific face beats the [dir="rtl"] rule in index.css.
    document.body.style.fontFamily = config.fontFamily || "";
    try {
      localStorage.setItem("language", language);
    } catch {
      /* private mode */
    }

    const timer = setTimeout(() => {
      document.body.classList.remove("lang-switching");
    }, 300); // matches the CSS transition

    return () => clearTimeout(timer);
  }, [language, config.dir, config.fontFamily]);

  // Locale registry. Falls back to the bundled list on any failure, so the
  // language switcher never disappears.
  useEffect(() => {
    let cancelled = false;
    if (!isSignedIn()) return undefined;

    api
      .get("/common/i18n/locales/")
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled && list.length) setLocales(list);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the delta for the active language. `no-cache` on the response means
  // the browser revalidates with If-None-Match and the server answers 304 when
  // nothing changed — cheap enough to run on every language switch.
  const fetchedRef = useRef(new Set());

  const loadDelta = useCallback(async (lang, { force = false } = {}) => {
    if (!lang || !isSignedIn()) return;
    if (!force && fetchedRef.current.has(lang)) return;
    fetchedRef.current.add(lang);

    try {
      const res = await api.get(`/common/i18n/bundle/?lang=${encodeURIComponent(lang)}`);
      const items = res?.data?.items || {};
      setDeltasByLang((prev) => ({ ...prev, [lang]: items }));
      writeCachedBundle(lang, { lang, version: res?.data?.version || "", items });
    } catch {
      // Keep whatever is cached; the JSON baseline still renders the UI.
      if (force) fetchedRef.current.delete(lang);
    }
  }, []);

  useEffect(() => {
    loadDelta(language);
  }, [language, loadDelta]);

  // Called by the translations admin page after a save so edits appear without
  // a reload — the bug that made the old editor feel broken.
  const refreshTranslations = useCallback(
    (lang) => loadDelta(lang || language, { force: true }),
    [loadDelta, language]
  );

  const translations = useMemo(
    () => ({
      ...en,
      ...(BASELINES[language] || {}),
      ...(deltasByLang[language] || {}),
    }),
    [language, deltasByLang]
  );

  const contextValue = useMemo(
    () => ({
      language,
      setLanguage,
      translations,
      dir: config.dir,
      languages,
      locales,
      refreshTranslations,
    }),
    [language, setLanguage, translations, config.dir, languages, locales, refreshTranslations]
  );

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  return useContext(LanguageContext);
};

export default LanguageProvider;
